import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgentLeaveService, AgentLeaveDto } from '../../../services/agent-leave.service';
import { AgentService, AgentDto } from '../../../services/agent.service';
import { AuthService } from '../../../services/auth.service';
import { filter, take } from 'rxjs';
import { RouterModule } from '@angular/router';
import { PaginationComponent } from '../../shared/pagination/pagination.component';

@Component({
  selector: 'app-agent-leaves',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PaginationComponent],
  templateUrl: './agent-leaves.component.html',
  styleUrl: './agent-leaves.component.css'
})
export class AgentLeavesComponent implements OnInit {
  leaves: AgentLeaveDto[] = [];
  agents: AgentDto[] = [];
  statusFilter = ''; // '' for all, '0' pending, '1' approved, '2' rejected, '3' unplanned
  selectedAgentId = '';
  startDateFilter = '';
  endDateFilter = '';
  isLoading = false;

  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 1;

  // Approve/Reject Comment Dialog
  showCommentModal = false;
  commentAction: 'approve' | 'reject' = 'approve';
  commentLeave: AgentLeaveDto | null = null;
  adminComment = '';

  notification = {
    show: false,
    message: '',
    type: 'success' as 'success' | 'error' | 'info'
  };

  constructor(
    private leaveService: AgentLeaveService,
    private agentService: AgentService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.authService.getMsalInitializedObservable().pipe(
      filter(initialized => initialized),
      take(1)
    ).subscribe(() => {
      this.loadAgents();
      this.loadLeaves();
    });
  }

  loadAgents(): void {
    // PageSize is 100 to stay within the backend maximum allowed validation limit of 100
    this.agentService.getAgentList(undefined, true, 0, 100).subscribe({
      next: (res) => {
        this.agents = res.items || [];
      },
      error: (err) => {
        console.error('Failed to load agents', err);
        this.showNotification('Failed to load agents', 'error');
      }
    });
  }

  loadLeaves(): void {
    this.isLoading = true;
    const statusVal = this.statusFilter !== '' ? parseInt(this.statusFilter, 10) : undefined;
    const pageIndex = this.currentPage - 1;

    this.leaveService.getLeavesForApproval(
      pageIndex,
      this.pageSize,
      statusVal,
      this.selectedAgentId || undefined,
      this.startDateFilter || undefined,
      this.endDateFilter || undefined
    ).subscribe({
      next: (res) => {
        this.leaves = res.items || [];
        this.totalCount = res.totalCount || 0;
        this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load leaves', err);
        this.showNotification('Failed to load leaves', 'error');
        this.isLoading = false;
      }
    });
  }

  onFilterChange(): void {
    this.currentPage = 1; // Reset to page 1 on filter change
    this.loadLeaves();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadLeaves();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadLeaves();
  }

  openCommentModal(leave: AgentLeaveDto, action: 'approve' | 'reject'): void {
    this.commentLeave = leave;
    this.commentAction = action;
    this.adminComment = '';
    this.showCommentModal = true;
  }

  closeCommentModal(): void {
    this.showCommentModal = false;
    this.commentLeave = null;
    this.adminComment = '';
  }

  submitCommentAction(): void {
    if (!this.commentLeave) return;

    const leaveId = this.commentLeave.id!;
    const agentId = this.commentLeave.agentId;

    if (this.commentAction === 'approve') {
      this.leaveService.approveLeave(leaveId, agentId, this.adminComment).subscribe({
        next: () => {
          this.showNotification('Leave approved successfully', 'success');
          this.closeCommentModal();
          this.loadLeaves();
        },
        error: (err) => {
          console.error('Failed to approve leave', err);
          this.showNotification(err.error?.message || 'Failed to approve leave', 'error');
        }
      });
    } else {
      if (!this.adminComment.trim()) {
        this.showNotification('Comment is required for rejection', 'error');
        return;
      }
      this.leaveService.rejectLeave(leaveId, agentId, this.adminComment).subscribe({
        next: () => {
          this.showNotification('Leave rejected successfully', 'success');
          this.closeCommentModal();
          this.loadLeaves();
        },
        error: (err) => {
          console.error('Failed to reject leave', err);
          this.showNotification(err.error?.message || 'Failed to reject leave', 'error');
        }
      });
    }
  }

  showNotification(message: string, type: 'success' | 'error' | 'info' = 'success'): void {
    this.notification = {
      show: true,
      message,
      type
    };
    setTimeout(() => {
      this.notification.show = false;
    }, 4000);
  }
}
